/** @format */
import {add, addMonths, differenceInDays, startOfMonth} from 'date-fns';

import {WIDGETS_LIST} from 'pages/Home/DigitalCast/constants/widgets';

import {Period} from '../../interfaces';
import {ChartPeriodsProps} from './interfaces';

export const getPeriods = (
  period: Pick<
    ChartPeriodsProps['period'],
    'startDate' | 'endDate' | 'repeatEvery' | 'isLinkedWithPeriodStart' | 'budgetPlan'
  >,
) => {
  const {startDate: start, endDate: end} = period;

  if (!start || !end || !period.endDate || !period.startDate) return [];

  const periods = [];
  let currentStart = start;

  while (currentStart < end) {
    let nextDate;

    if (period.repeatEvery === 'weekly') {
      if (period.isLinkedWithPeriodStart) {
        const daysUntilMonday = (8 - currentStart.getDay()) % 7;
        nextDate = add(currentStart, {
          days: daysUntilMonday > 0 ? daysUntilMonday - 1 : 6,
        });
      } else {
        nextDate = add(currentStart, {days: 6});
      }
    } else {
      if (period.isLinkedWithPeriodStart) {
        nextDate = add(startOfMonth(addMonths(currentStart, 1)), {days: -1});
      } else {
        const nextMonth = addMonths(currentStart, 1);
        const originalDay = start.getDate();
        const daysInNextMonth = new Date(
          nextMonth.getFullYear(),
          nextMonth.getMonth() + 1,
          0,
        ).getDate();
        const targetDay = Math.min(originalDay, daysInNextMonth);
        nextDate = add(new Date(nextMonth.getFullYear(), nextMonth.getMonth(), targetDay), {
          days: -1,
        });
      }
    }

    const periodEnd = nextDate > end ? end : nextDate;
    const days = differenceInDays(periodEnd, currentStart) + 1;

    periods.push({
      start: currentStart,
      end: periodEnd,
      days,
      budget: period.budgetPlan,
      dailyBudget: period.budgetPlan ? Math.round(period.budgetPlan / days) : null,
      offset:
        (differenceInDays(currentStart, period.startDate) /
          (differenceInDays(period.endDate, period.startDate) + 1)) *
        100,
      width: (days / (differenceInDays(period.endDate, period.startDate) + 1)) * 100,
    });

    currentStart = add(periodEnd, {days: 1});
  }

  return periods;
};

export const MIN_PERIOD_WIDTH_PIXELS = 100;

export const getCplChartText = (period: Period): string => {
  const mainCpl = period?.kpis?.find(({primary}) => primary);
  const formattedCplId = mainCpl?.id?.toLowerCase();

  const [mainCplName, mainCplValue] = [mainCpl?.name || '', mainCpl?.value || ''];

  if (!formattedCplId) {
    return '';
  }

  if (formattedCplId in WIDGETS_LIST) {
    const {label: cplName, token: cplSymbol} = WIDGETS_LIST[formattedCplId];

    return `${cplName} ${mainCplValue} ${cplSymbol || ''}`;
  }

  const cplText = `${mainCplName} ${mainCplValue}`;

  return cplText;
};
